import { computed, inject, Injectable, signal } from '@angular/core';
import { DEFAULT_SERVER_ID } from '../../../environments/default-server';
import { StorageService } from './storage.service';

export interface ApiServer {
  readonly id: string;
  readonly label: string;
  readonly baseUrl: string;
}

/**
 * Fixed allowlist of backends the app may talk to. Never accept a free-form URL here: the
 * auth token is sent to whichever server is selected.
 */
export const API_SERVERS: readonly ApiServer[] = [
  { id: 'production', label: 'Production', baseUrl: 'https://api.iofteam.com/api/v1/' },
  { id: 'staging', label: 'Staging', baseUrl: 'https://api.staging.iofteam.com/api/v1/' },
];

export type ApiServerId = 'production' | 'staging';

/** Production unless the build swaps default-server.ts (see the development configuration). */
export const DEFAULT_API_SERVER_ID: ApiServerId = DEFAULT_SERVER_ID;

const SERVER_KEY = 'digimep.apiServer';

/**
 * Which backend the app talks to. Defaults to production for every user; only super admins
 * can switch (Profile page). Services keep building URLs from environment.BASE_URL and
 * apiServerInterceptor rewrites them to the selected server.
 */
@Injectable({ providedIn: 'root' })
export class ApiServerService {
  private readonly storage = inject(StorageService);
  private readonly currentSignal = signal<ApiServer>(this.load());

  readonly current = this.currentSignal.asReadonly();
  readonly isDefault = computed(() => this.currentSignal().id === DEFAULT_API_SERVER_ID);

  select(id: string): void {
    const server = findServer(id) ?? defaultServer();
    this.currentSignal.set(server);
    if (server.id === DEFAULT_API_SERVER_ID) this.storage.remove(SERVER_KEY);
    else this.storage.set(SERVER_KEY, server.id);
  }

  /**
   * Point a URL built for any known server at the selected one. URLs for other hosts
   * (images, store links) are returned unchanged.
   */
  rewrite(url: string): string {
    const target = this.currentSignal().baseUrl;
    // Longest base first so the staging host is never mistaken for a prefix match of another.
    const source = [...API_SERVERS]
      .sort((a, b) => b.baseUrl.length - a.baseUrl.length)
      .find((s) => url.startsWith(s.baseUrl));
    return source && source.baseUrl !== target ? target + url.slice(source.baseUrl.length) : url;
  }

  private load(): ApiServer {
    // Unknown or tampered values fall back to production.
    return findServer(this.storage.get<string>(SERVER_KEY) ?? '') ?? defaultServer();
  }
}

function findServer(id: string): ApiServer | undefined {
  return API_SERVERS.find((s) => s.id === id);
}

function defaultServer(): ApiServer {
  return findServer(DEFAULT_API_SERVER_ID)!;
}
